import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import ProdutosQuimicosPainel from '../components/ProdutosQuimicosPainel';

/* ============================================================
   REQUISITOS LEGAIS SST (SGI — ISO 45001, 6.1.3)
   Matriz de diplomas aplicáveis + obrigações de conformidade
   avaliadas, fiel à Matriz de Requisitos_Avaliação de
   conformidade Legal. A APCER exige esta lista na certificação.
   ============================================================ */

const APLICAVEL = {
    'Sim': { bg: '#dcfce7', fg: '#166534' },
    'Potencialmente': { bg: '#fef9c3', fg: '#854d0e' },
    'Informativo': { bg: '#e0f2fe', fg: '#075985' },
    'Não': { bg: '#f1f5f9', fg: '#64748b' },
};
const CONFORMIDADE = {
    'Conforme': { bg: '#dcfce7', fg: '#166534' },
    'Não Conforme': { bg: '#fee2e2', fg: '#991b1b' },
    'Em Análise': { bg: '#fef9c3', fg: '#854d0e' },
    'Informativo': { bg: '#e0f2fe', fg: '#075985' },
    'Não Aplicável': { bg: '#f1f5f9', fg: '#64748b' },
};
const fmtData = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return '—'; } };

// Um documento que caducou vale o mesmo que documento nenhum — por isso o
// estado da validade é mais visível do que a própria data.
const VALIDADE = {
    caducado: { bg: '#fee2e2', fg: '#991b1b', label: 'Caducado' },
    a_caducar: { bg: '#fef3c7', fg: '#92400e', label: 'A caducar' },
    valido: { bg: '#dcfce7', fg: '#166534', label: 'Válido' },
    // Guardado mas fora dos alertas — cinzento, para não parecer um problema.
    arquivado: { bg: '#f1f5f9', fg: '#475569', label: 'Arquivado' },
    // Há um mais recente da mesma pessoa que o substituiu. Também cinzento:
    // caducou, mas não há nada a renovar. O rótulo escrito aqui é o masculino;
    // quem concorda com o género do documento é rotuloValidade, logo abaixo.
    substituido: { bg: '#f1f5f9', fg: '#475569', label: '🗂️ Substituído' },
};

/* "Ficha" é feminina e hoje só as fichas de aptidão são substituídas, pelo que
   "Substituído" aparecia em todas as linhas à vista. O resto do mapa não muda:
   "documento" é masculino e serve para as outras categorias. */
const rotuloValidade = (estado, categoria) => (
    estado === 'substituido' && categoria === 'fichas_aptidao'
        ? '🗂️ Substituída'
        : VALIDADE[estado]?.label
);

/* ---------- Carregar / editar uma evidência ---------- */
function EvidenciaModal({ evidencia, categorias, obrigacoes, onClose, onSaved }) {
    const novo = !evidencia?.id;
    const [form, setForm] = useState({
        categoria: evidencia?.categoria || 'seguros',
        titulo: evidencia?.titulo || '',
        entidade: evidencia?.entidade || '',
        data_documento: (evidencia?.data_documento || '').substring(0, 10),
        validade: (evidencia?.validade || '').substring(0, 10),
        obrigacao_id: evidencia?.obrigacao_id || '',
        trabalhador_id: evidencia?.trabalhador_id || '',
        observacoes: evidencia?.observacoes || '',
        arquivada: !!evidencia?.arquivada,
        visivel_colaboradores: !!evidencia?.visivel_colaboradores,
    });
    // A ficha de aptidão e a descrição de funções são de uma pessoa. Sem as
    // ligar ao trabalhador, o documento entra órfão e a ficha dele não o mostra.
    const [trabalhadores, setTrabalhadores] = useState([]);
    const [ficheiro, setFicheiro] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const cat = categorias.find(c => c.valor === form.categoria);

    useEffect(() => {
        if (!cat?.por_trabalhador || trabalhadores.length) return;
        api.get('/trabalhadores').then(r => setTrabalhadores(
            (Array.isArray(r.data) ? r.data : r.data?.trabalhadores || [])
                .slice().sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt'))
        )).catch(() => { });
    }, [cat?.por_trabalhador, trabalhadores.length]);

    const guardar = async () => {
        if (novo && !ficheiro) { setErr('É obrigatório anexar um ficheiro.'); return; }
        setSaving(true); setErr('');
        try {
            if (novo) {
                const fd = new FormData();
                Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ''));
                fd.append('ficheiro', ficheiro);
                await api.post('/conformidade-evidencias', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            } else {
                await api.put(`/conformidade-evidencias/${evidencia.id}`, form);
            }
            onSaved(); onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao guardar.'); }
        finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 640 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{novo ? 'Carregar evidência' : 'Editar evidência'}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                <div style={{ display: 'grid', gap: 12 }}>
                    <div data-liquid-grid="true" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <label data-liquid-label="true" style={lbl}>Categoria</label>
                            <select value={form.categoria} onChange={e => upd('categoria', e.target.value)} style={{ ...inp, width: '100%' }}>
                                {categorias.map(c => <option key={c.valor} value={c.valor}>{c.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <label data-liquid-label="true" style={lbl}>Entidade emissora</label>
                            <input value={form.entidade} onChange={e => upd('entidade', e.target.value)}
                                placeholder="Fidelidade, Securilabor, IMPIC…" style={{ ...inp, width: '100%' }} />
                        </div>
                    </div>

                    <div>
                        <label data-liquid-label="true" style={lbl}>Título</label>
                        <input value={form.titulo} onChange={e => upd('titulo', e.target.value)}
                            placeholder="Apólice AT 63893525" style={{ ...inp, width: '100%' }} />
                    </div>

                    <div data-liquid-grid="true" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <label data-liquid-label="true" style={lbl}>Data do documento</label>
                            <input type="date" value={form.data_documento} onChange={e => upd('data_documento', e.target.value)} style={{ ...inp, width: '100%' }} />
                        </div>
                        <div>
                            <label data-liquid-label="true" style={lbl}>Válido até {cat?.exige_validade && <span style={{ color: '#b45309' }}>· recomendado</span>}</label>
                            <input type="date" value={form.validade} onChange={e => upd('validade', e.target.value)} style={{ ...inp, width: '100%' }} />
                        </div>
                    </div>

                    {cat?.por_trabalhador && (
                        <div>
                            <label data-liquid-label="true" style={lbl}>Trabalhador a que o documento pertence <span style={{ color: '#b45309' }}>· sem isto o documento fica órfão</span></label>
                            <select value={form.trabalhador_id} onChange={e => upd('trabalhador_id', e.target.value)} style={{ ...inp, width: '100%' }}>
                                <option value="">— não associar —</option>
                                {trabalhadores.map(t => (
                                    <option key={t.id} value={t.id}>{t.nome}{t.ativo === false ? ' (inativo)' : ''}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div>
                        <label data-liquid-label="true" style={lbl}>Obrigação legal que este documento prova</label>
                        <select value={form.obrigacao_id} onChange={e => upd('obrigacao_id', e.target.value)} style={{ ...inp, width: '100%' }}>
                            <option value="">— não associar —</option>
                            {obrigacoes.map(o => (
                                <option key={o.id} value={o.id}>
                                    {o.diploma} {o.artigo ? `· ${o.artigo}` : ''} — {(o.obrigacao || '').substring(0, 80)}
                                </option>
                            ))}
                        </select>
                    </div>

                    {novo && (
                        <div>
                            <label data-liquid-label="true" style={lbl}>Ficheiro</label>
                            <input type="file" onChange={e => setFicheiro(e.target.files?.[0] || null)} style={{ ...inp, width: '100%' }} />
                        </div>
                    )}

                    {/* O que os trabalhadores consultam no telemóvel (separador Documentos).
                        Nunca num documento de uma pessoa: o servidor recusa-o também. */}
                    {!cat?.por_trabalhador && (
                        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.85rem',
                            color: '#334155', cursor: 'pointer' }}>
                            <input type="checkbox" checked={!!form.visivel_colaboradores}
                                onChange={e => upd('visivel_colaboradores', e.target.checked)} style={{ marginTop: 3 }} />
                            <span><strong><LiquidText>👷 Visível aos colaboradores</LiquidText></strong> — aparece no separador Documentos da app
                                no telemóvel (avaliação de riscos, instruções de trabalho, medidas de autoproteção…).</span>
                        </label>
                    )}

                    {/* Arquivado: o documento fica guardado — muitas vezes a lei obriga,
                        como às fichas de aptidão de quem saiu — mas deixa de pedir
                        atenção nos contadores de caducados. */}
                    {!novo && (
                        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.85rem',
                            color: '#334155', cursor: 'pointer' }}>
                            <input type="checkbox" checked={!!form.arquivada}
                                onChange={e => upd('arquivada', e.target.checked)} style={{ marginTop: 3 }} />
                            <span><strong>Arquivado</strong> — guarda-se, mas já não conta para os alertas de caducado
                                (por exemplo, a ficha de alguém que deixou a empresa).</span>
                        </label>
                    )}

                    <div>
                        <label data-liquid-label="true" style={lbl}>Observações</label>
                        <textarea value={form.observacoes} onChange={e => upd('observacoes', e.target.value)} rows={2}
                            style={{ ...inp, width: '100%', resize: 'vertical' }} />
                    </div>

                    {err && <div style={{ color: '#b91c1c', fontSize: '0.85rem' }}>{err}</div>}

                    <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                        <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={btnPri}>{saving ? 'A guardar…' : 'Guardar'}</button>
                    </div>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Evidências, agrupadas como as pastas do arquivo ---------- */
function EvidenciasVista({ obrigacoes }) {
    const [categorias, setCategorias] = useState([]);
    const [resumo, setResumo] = useState([]);
    const [itens, setItens] = useState([]);
    const [catFiltro, setCatFiltro] = useState('');
    const [editar, setEditar] = useState(null);
    const [loading, setLoading] = useState(true);
    const [mostrarSubstituidos, setMostrarSubstituidos] = useState(false);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [c, r, l] = await Promise.all([
                api.get('/conformidade-evidencias/categorias'),
                api.get('/conformidade-evidencias/resumo'),
                api.get('/conformidade-evidencias', { params: catFiltro ? { categoria: catFiltro } : {} }),
            ]);
            setCategorias(c.data); setResumo(r.data); setItens(l.data);
        } catch { /* silencioso: o ecrã mostra o vazio */ }
        finally { setLoading(false); }
    }, [catFiltro]);

    useEffect(() => { carregar(); }, [carregar]);

    /* O ficheiro sai por endpoint autenticado, pelo que não se pode abrir com
       um window.open direto: a nova janela não leva o token e o servidor
       responde "Token não fornecido". Busca-se como blob e abre-se daí.
       Nem tudo é PDF — há folhas de cálculo — e essas descarregam com o nome
       original em vez de abrirem numa página em branco. */
    const abrir = async (ev) => {
        try {
            const r = await api.get(`/conformidade-evidencias/${ev.id}/ficheiro`, { responseType: 'blob' });
            const tipo = r.data.type || 'application/octet-stream';
            const url = window.URL.createObjectURL(new Blob([r.data], { type: tipo }));
            if (tipo.includes('pdf')) {
                window.open(url, '_blank');
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = ev.nome_original || 'documento';
                a.click();
            }
            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        } catch { alert('Não foi possível abrir o documento.'); }
    };
    const apagar = async (id) => {
        if (!window.confirm('Apagar esta evidência e o respetivo ficheiro?')) return;
        try { await api.delete(`/conformidade-evidencias/${id}`); carregar(); } catch { alert('Erro ao apagar.'); }
    };

    const totalCaducadas = resumo.reduce((n, c) => n + c.caducadas, 0);
    const totalACaducar = resumo.reduce((n, c) => n + c.a_caducar, 0);

    /* Decisão de 15-09-2026: um documento substituído por outro mais recente
       da mesma pessoa sai da vista normal. Fica a um clique ("Mostrar
       substituídos") e na aba Histórico da ficha do colaborador — nada se
       apaga. Os contadores de caducados não mudam com isto: o servidor já não
       conta os substituídos (utils/substituicao.js). */
    const nSubstituidos = itens.filter(e => e.substituida).length;
    const visiveis = mostrarSubstituidos ? itens : itens.filter(e => !e.substituida);

    return (
        <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                {/* Com uma pasta escolhida, carregar vai para essa pasta: era o
                    que faltava dizer a quem clica no cartão e espera poder lá pôr algo. */}
                <button data-liquid-primary="true" onClick={() => setEditar(catFiltro ? { categoria: catFiltro } : {})} style={btnPri}>
                    + Carregar {catFiltro ? `para ${(resumo.find(x => x.valor === catFiltro) || {}).label || 'esta pasta'}` : 'evidência'}
                </button>
                {totalCaducadas > 0 && <span data-liquid-tone={liquidTone(VALIDADE.caducado.bg)} data-liquid-badge="true" style={{ ...badge, background: VALIDADE.caducado.bg, color: VALIDADE.caducado.fg }}>{totalCaducadas} caducado(s)</span>}
                {totalACaducar > 0 && <span data-liquid-tone={liquidTone(VALIDADE.a_caducar.bg)} data-liquid-badge="true" style={{ ...badge, background: VALIDADE.a_caducar.bg, color: VALIDADE.a_caducar.fg }}>{totalACaducar} a caducar em 60 dias</span>}
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                    As categorias reproduzem as pastas de <code>Conformidade Legal</code> no arquivo antigo.
                </span>
            </div>

            {/* Pastas */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 8, marginBottom: 16 }}>
                {resumo.map(c => {
                    const ativo = catFiltro === c.valor;
                    /* O número que se vê na lista: com os substituídos escondidos
                       (o normal), sem eles; com o alternador ligado, o total —
                       senão o cartão dizia 38 com 42 linhas na tabela. */
                    const emVista = mostrarSubstituidos ? c.total : c.total - (c.substituidas || 0);
                    return (
                        <div key={c.valor} onClick={() => setCatFiltro(ativo ? '' : c.valor)}
                            style={{
                                textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                                background: ativo ? '#eef2ff' : '#fff',
                                border: `2px solid ${ativo ? '#6366f1' : '#e2e8f0'}`,
                                display: 'flex', flexDirection: 'column',
                            }}>
                            <div style={{ fontSize: '0.8rem', color: '#334155', fontWeight: 600, lineHeight: 1.3 }}><LiquidText>{c.label}</LiquidText></div>
                            <div style={{ marginTop: 5, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '1.15rem', fontWeight: 800, color: emVista ? '#1e293b' : '#cbd5e1' }}>{emVista}</span>
                                {c.caducadas > 0 && <span data-liquid-tone={liquidTone(VALIDADE.caducado.bg)} data-liquid-badge="true" style={{ ...badge, background: VALIDADE.caducado.bg, color: VALIDADE.caducado.fg, fontSize: '0.68rem' }}>{c.caducadas} caducado</span>}
                                {c.a_caducar > 0 && <span data-liquid-tone={liquidTone(VALIDADE.a_caducar.bg)} data-liquid-badge="true" style={{ ...badge, background: VALIDADE.a_caducar.bg, color: VALIDADE.a_caducar.fg, fontSize: '0.68rem' }}>{c.a_caducar} a caducar</span>}
                                <div style={{ flex: 1 }} />
                                <button
                                    title={`Carregar documento para ${c.label}`}
                                    onClick={e => { e.stopPropagation(); setEditar({ categoria: c.valor }); }}
                                    style={{
                                        border: '1px solid #cbd5e1', background: '#fff', color: '#475569',
                                        borderRadius: 6, cursor: 'pointer', fontSize: '0.74rem',
                                        padding: '2px 8px', fontWeight: 600, whiteSpace: 'nowrap',
                                    }}>+ carregar</button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Na pasta FDS, o inventário de produtos químicos que os trabalhadores
                registam no telemóvel — é aqui que se lhes juntam os PDF */}
            {catFiltro === 'fds' && <ProdutosQuimicosPainel onDocumentosAlterados={carregar} />}

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p> : (
                <>
                {nSubstituidos > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                        <button data-liquid-secondary="true" onClick={() => setMostrarSubstituidos(v => !v)} aria-pressed={mostrarSubstituidos}
                            style={{ ...btnSec, padding: '5px 12px', fontSize: '0.78rem' }}>
                            {mostrarSubstituidos ? `Esconder substituídos (${nSubstituidos})` : `Mostrar substituídos (${nSubstituidos})`}
                        </button>
                    </div>
                )}
                <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                    <table className="standard-table">
                        <thead>
                            <tr>
                                <th>Categoria</th>
                                <th>Documento</th>
                                <th>Entidade</th>
                                <th>Data</th>
                                <th>Validade</th>
                                <th>Prova a obrigação</th>
                                <th style={{ textAlign: 'right' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {visiveis.length === 0 && (
                                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: '32px 0' }}>
                                    {nSubstituidos > 0
                                        ? 'Aqui só há documentos substituídos — use "Mostrar substituídos" para os ver.'
                                        : catFiltro ? 'Sem evidências nesta categoria.' : 'Ainda não há evidências carregadas.'}
                                </td></tr>
                            )}
                            {visiveis.map(e => {
                                const v = VALIDADE[e.estado_validade];
                                const ficha = e.categoria === 'fichas_aptidao';
                                return (
                                    <tr key={e.id}>
                                        <td style={{ fontSize: '0.8rem', color: '#475569', whiteSpace: 'nowrap' }}>{e.categoria_label}</td>
                                        <td style={{ fontWeight: 600, color: e.substituida ? '#64748b' : undefined }}>
                                            <LiquidText>{e.titulo}</LiquidText>
                                            {e.visivel_colaboradores && !e.arquivada && (
                                                <span data-liquid-tone={liquidTone('#e0f2fe')} data-liquid-badge="true" title="Visível aos colaboradores no separador Documentos do telemóvel"
                                                    style={{ ...badge, background: '#e0f2fe', color: '#075985', marginLeft: 6, fontSize: '0.68rem' }}><LiquidText>👷 app</LiquidText></span>
                                            )}
                                            {e.observacoes && <div style={{ fontWeight: 400, fontSize: '0.78rem', color: '#94a3b8' }}>{e.observacoes.substring(0, 90)}</div>}
                                        </td>
                                        <td style={{ fontSize: '0.84rem' }}>{e.entidade || '—'}</td>
                                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.84rem' }}>{fmtData(e.data_documento)}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            {v ? (
                                                <span data-liquid-tone={liquidTone(v.bg)} data-liquid-badge="true" style={{ ...badge, background: v.bg, color: v.fg }}>
                                                    {rotuloValidade(e.estado_validade, e.categoria)} · {fmtData(e.validade)}
                                                </span>
                                            ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                                            {e.substituida && e.substituida_por && (
                                                <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 3 }}>
                                                    {ficha ? 'substituída pela' : 'substituído pelo'} de {fmtData(e.substituida_por.data_documento)}
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ maxWidth: 230, fontSize: '0.78rem', color: '#64748b' }}>
                                            {e.obrigacao_diploma
                                                ? <>{e.obrigacao_diploma}{e.obrigacao_artigo ? ` · ${e.obrigacao_artigo}` : ''}</>
                                                : <span style={{ color: '#cbd5e1' }}>—</span>}
                                        </td>
                                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <button data-liquid-secondary="true" onClick={() => abrir(e)} style={{ ...btnSec, padding: '4px 9px', fontSize: '0.76rem' }}>Abrir</button>{' '}
                                            <button data-liquid-secondary="true" onClick={() => setEditar(e)} style={{ ...btnSec, padding: '4px 9px', fontSize: '0.76rem' }}>Editar</button>{' '}
                                            <button data-liquid-secondary="true" onClick={() => apagar(e.id)} style={{ ...btnSec, padding: '4px 9px', fontSize: '0.76rem', color: '#b91c1c' }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                </>
            )}

            {editar && <EvidenciaModal evidencia={editar} categorias={categorias} obrigacoes={obrigacoes}
                onClose={() => setEditar(null)} onSaved={carregar} />}
        </div>
    );
}

/* ---------- Editar obrigação ---------- */
function ObrigacaoModal({ obrigacao, onClose, onSaved }) {
    const [form, setForm] = useState({ ...obrigacao });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const guardar = async () => {
        setSaving(true); setErr('');
        try {
            await api.put(`/sistema-integrado/req-legais/obrigacoes/${obrigacao.id}`, form);
            onSaved(); onClose();
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao guardar'); }
        finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 680 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Avaliar obrigação</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 10 }}>
                    <strong>{form.diploma}</strong> {form.artigo && `· ${form.artigo}`} · {form.area}
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Dever / obrigação</label>
                    <textarea value={form.obrigacao || ''} onChange={e => upd('obrigacao', e.target.value)} rows={3} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Conformidade</label>
                    <select value={form.conformidade || 'Em Análise'} onChange={e => upd('conformidade', e.target.value)} style={{ ...inp, width: 220 }}>
                        {Object.keys(CONFORMIDADE).map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Evidências / observações</label>
                    <textarea value={form.evidencias || ''} onChange={e => upd('evidencias', e.target.value)} rows={3} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                <div style={{ marginBottom: 6 }}>
                    <label data-liquid-label="true" style={lbl}>Ações a desenvolver</label>
                    <textarea value={form.acoes || ''} onChange={e => upd('acoes', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                {err && <div style={{ color: '#e53e3e', margin: '10px 0', fontSize: '0.85rem' }}>{err}</div>}
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Guardar'}</button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Componente principal ---------- */
const RequisitosLegais = () => {
    const anoAtual = new Date().getFullYear();
    /* As evidências abrem primeiro: são o que se mostra numa auditoria. A
       matriz e as obrigações são o enquadramento, consultam-se a seguir. */
    const [vista, setVista] = useState('evidencias'); // evidencias | obrigacoes | matriz
    const [nEvidencias, setNEvidencias] = useState(null);
    const [matriz, setMatriz] = useState([]);
    const [obrigacoes, setObrigacoes] = useState([]);
    const [config, setConfig] = useState({});
    const [loading, setLoading] = useState(true);
    const [pesquisa, setPesquisa] = useState('');
    const [confFiltro, setConfFiltro] = useState('');
    const [areaFiltro, setAreaFiltro] = useState('');
    const [aplicFiltro, setAplicFiltro] = useState('');
    const [editar, setEditar] = useState(null);
    const [editandoDatas, setEditandoDatas] = useState(false);
    const [datas, setDatas] = useState({});
    const [ano, setAno] = useState(anoAtual);
    const [anosExistentes, setAnosExistentes] = useState([]);
    const [duplicando, setDuplicando] = useState(false);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [r, o, a, ev] = await Promise.all([
                api.get('/sistema-integrado/req-legais'),
                api.get('/sistema-integrado/req-legais/obrigacoes', { params: { ano } }),
                api.get('/sistema-integrado/req-legais/obrigacoes-anos'),
                api.get('/conformidade-evidencias/resumo').catch(() => ({ data: [] })),
            ]);
            setMatriz(r.data.matriz || []);
            setConfig(r.data.config || {});
            setDatas(r.data.config || {});
            setObrigacoes(o.data || []);
            setAnosExistentes(Array.isArray(a.data) ? a.data : []);
            // Sem os substituídos, que a lista esconde por omissão: o número do
            // separador tem de bater com o que se vê ao abri-lo.
            setNEvidencias((ev.data || []).reduce((n, c) => n + (c.total || 0) - (c.substituidas || 0), 0));
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [ano]);
    useEffect(() => { carregar(); }, [carregar]);

    const anos = [];
    for (let a = anoAtual + 1; a >= anoAtual - 4; a--) anos.push(a);
    anosExistentes.forEach(a => { if (!anos.includes(a)) anos.push(a); });
    anos.sort((x, y) => y - x);

    const anoAnteriorComDados = anosExistentes.filter(a => a < ano).sort((x, y) => y - x)[0] || null;

    const copiarAvaliacao = async () => {
        if (!anoAnteriorComDados) return;
        if (!window.confirm(`Copiar a avaliação de ${anoAnteriorComDados} para ${ano}? A conformidade e evidências vêm como ponto de partida para a reavaliação.`)) return;
        setDuplicando(true);
        try {
            const r = await api.post('/sistema-integrado/req-legais/obrigacoes/duplicar-ano', { de: anoAnteriorComDados, para: ano });
            alert(r.data.message);
            carregar();
        } catch (e) { alert(e.response?.data?.error || 'Erro ao copiar avaliação'); }
        finally { setDuplicando(false); }
    };

    const guardarDatas = async () => {
        try {
            await api.put('/sistema-integrado/req-legais/config', datas);
            setEditandoDatas(false);
            carregar();
        } catch { alert('Erro ao guardar datas'); }
    };

    const areas = [...new Set(obrigacoes.map(o => o.area).filter(Boolean))].sort();
    const contagem = {};
    obrigacoes.forEach(o => { contagem[o.conformidade] = (contagem[o.conformidade] || 0) + 1; });

    let obrVisiveis = obrigacoes;
    if (confFiltro) obrVisiveis = obrVisiveis.filter(o => o.conformidade === confFiltro);
    if (areaFiltro) obrVisiveis = obrVisiveis.filter(o => o.area === areaFiltro);
    let matVisiveis = matriz;
    if (aplicFiltro) matVisiveis = matVisiveis.filter(m => m.aplicavel === aplicFiltro);
    if (pesquisa.trim()) {
        const t = pesquisa.trim().toLowerCase();
        obrVisiveis = obrVisiveis.filter(o => (o.diploma || '').toLowerCase().includes(t) || (o.obrigacao || '').toLowerCase().includes(t) || (o.evidencias || '').toLowerCase().includes(t));
        matVisiveis = matVisiveis.filter(m => (m.requisito || '').toLowerCase().includes(t) || (m.sumario || '').toLowerCase().includes(t) || (m.tema || '').toLowerCase().includes(t));
    }

    return (
        <div>
            {/* Datas da avaliação */}
            <div data-liquid-notice="true" data-liquid-tone={liquidTone('#eef2ff')} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '10px 16px', marginBottom: 14 }}>
                <div style={{ fontSize: '0.88rem', color: '#3730a3' }}>
                    {editandoDatas ? (
                        <span style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            Última avaliação: <input type="date" value={datas.conf_legal_avaliacao || ''} onChange={e => setDatas(p => ({ ...p, conf_legal_avaliacao: e.target.value }))} style={inp} />
                            Próxima: <input type="date" value={datas.conf_legal_proxima || ''} onChange={e => setDatas(p => ({ ...p, conf_legal_proxima: e.target.value }))} style={inp} />
                            <button data-liquid-primary="true" onClick={guardarDatas} style={btnPri}>Guardar</button>
                            <button data-liquid-secondary="true" onClick={() => { setEditandoDatas(false); setDatas(config); }} style={btnSec}>Cancelar</button>
                        </span>
                    ) : (
                        <>
                            <strong>Avaliação de conformidade legal:</strong> última em {fmtData(config.conf_legal_avaliacao)} · próxima em <strong>{fmtData(config.conf_legal_proxima)}</strong> (aparece no painel de validades)
                            <button data-liquid-secondary="true" onClick={() => setEditandoDatas(true)} style={{ ...btnSec, marginLeft: 10, padding: '3px 10px', fontSize: '0.76rem' }}>Editar</button>
                        </>
                    )}
                </div>
            </div>

            {/* Cartões de conformidade — clicáveis. Contam obrigações do ano
                escolhido, pelo que não fazem sentido sobre as evidências: os
                documentos não são de um ano só. */}
            <div style={{ display: vista === 'evidencias' ? 'none' : 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {Object.entries(CONFORMIDADE).map(([k, cor]) => (
                    <button key={k} onClick={() => { setVista('obrigacoes'); setConfFiltro(confFiltro === k ? '' : k); }}
                        style={{
                            flex: '1 1 110px', background: cor.bg, borderRadius: 10, padding: '9px 12px', cursor: 'pointer', textAlign: 'center',
                            border: `2px solid ${confFiltro === k ? cor.fg : 'transparent'}`,
                        }}>
                        <div style={{ fontSize: '1.3rem', fontWeight: 800, color: cor.fg }}>{contagem[k] || 0}</div>
                        <div style={{ fontSize: '0.76rem', color: cor.fg }}>{k}</div>
                    </button>
                ))}
            </div>

            {/* Controlo */}
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
                {vista !== 'evidencias' && (
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ano da avaliação</label>
                        <select value={ano} onChange={e => setAno(parseInt(e.target.value, 10))} style={inp}>
                            {anos.map(a => <option key={a} value={a}>{a}{anosExistentes.includes(a) ? '' : ' (vazio)'}</option>)}
                        </select>
                    </div>
                )}
                <div className="shared-subnav" style={{ margin: 0 }}>
                    <button className={`shared-subnav-btn ${vista === 'evidencias' ? 'active' : ''}`} onClick={() => setVista('evidencias')}>Evidências{nEvidencias != null ? ` (${nEvidencias})` : ''}</button>
                    <button className={`shared-subnav-btn ${vista === 'obrigacoes' ? 'active' : ''}`} onClick={() => setVista('obrigacoes')}>Obrigações avaliadas ({obrigacoes.length})</button>
                    <button className={`shared-subnav-btn ${vista === 'matriz' ? 'active' : ''}`} onClick={() => setVista('matriz')}>Matriz de diplomas ({matriz.length})</button>
                </div>
                {vista === 'obrigacoes' && !loading && obrigacoes.length === 0 && anoAnteriorComDados && (
                    <button data-liquid-secondary="true" onClick={copiarAvaliacao} disabled={duplicando} style={btnSec}>
                        <LiquidText>{duplicando ? 'A copiar…' : `⧉ Copiar avaliação de ${anoAnteriorComDados}`}</LiquidText>
                    </button>
                )}
                <div style={{ flex: 1 }} />
                {/* As evidências filtram-se pelas próprias pastas, não por estes campos. */}
                {vista === 'obrigacoes' && (
                    <div>
                        <label data-liquid-label="true" style={lbl}>Área</label>
                        <select value={areaFiltro} onChange={e => setAreaFiltro(e.target.value)} style={{ ...inp, maxWidth: 260 }}>
                            <option value="">Todas</option>
                            {areas.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>
                )}
                {vista === 'matriz' && (
                    <div>
                        <label data-liquid-label="true" style={lbl}>Aplicável</label>
                        <select value={aplicFiltro} onChange={e => setAplicFiltro(e.target.value)} style={inp}>
                            <option value="">Todos</option>
                            {Object.keys(APLICAVEL).map(k => <option key={k} value={k}>{k}</option>)}
                        </select>
                    </div>
                )}
                {vista !== 'evidencias' && (
                    <div>
                        <label data-liquid-label="true" style={lbl}>Pesquisar</label>
                        <input value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="Diploma, texto…" style={{ ...inp, width: 200 }} />
                    </div>
                )}
            </div>

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p> : vista === 'evidencias' ? (
                <EvidenciasVista obrigacoes={obrigacoes} />
            ) : vista === 'obrigacoes' ? (
                <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                    <table className="standard-table">
                        <thead>
                            <tr>
                                <th>Diploma</th>
                                <th>Artigo</th>
                                <th>Obrigação</th>
                                <th>Conformidade</th>
                                <th>Evidências</th>
                                <th style={{ textAlign: 'right' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {obrVisiveis.length === 0 && (
                                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: '32px 0' }}>
                                    {obrigacoes.length === 0
                                        ? `Sem avaliação registada em ${ano}. Use "Copiar avaliação" do ano anterior para iniciar a reavaliação.`
                                        : 'Nenhuma obrigação corresponde aos filtros.'}
                                </td></tr>
                            )}
                            {obrVisiveis.map(o => {
                                const cor = CONFORMIDADE[o.conformidade] || CONFORMIDADE['Em Análise'];
                                return (
                                    <tr key={o.id}>
                                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{o.diploma || '—'}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{o.artigo || '—'}</td>
                                        <td style={{ maxWidth: 380, fontSize: '0.85rem' }}>{(o.obrigacao || '').substring(0, 200)}{(o.obrigacao || '').length > 200 ? '…' : ''}</td>
                                        <td><span data-liquid-tone={liquidTone(cor.bg)} data-liquid-badge="true" style={{ ...badge, background: cor.bg, color: cor.fg }}>{o.conformidade}</span></td>
                                        <td style={{ maxWidth: 220, fontSize: '0.82rem', color: '#64748b' }}>{(o.evidencias || '').substring(0, 110)}{(o.evidencias || '').length > 110 ? '…' : ''}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button data-liquid-secondary="true" onClick={() => setEditar(o)} style={{ ...btnSec, padding: '4px 9px', fontSize: '0.76rem' }}>Avaliar</button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                    <table className="standard-table">
                        <thead>
                            <tr>
                                <th>Descritor</th>
                                <th>Tema</th>
                                <th>Requisito / norma</th>
                                <th>Sumário</th>
                                <th>Estado</th>
                                <th>Aplicável</th>
                            </tr>
                        </thead>
                        <tbody>
                            {matVisiveis.map(m => {
                                const cor = APLICAVEL[m.aplicavel] || APLICAVEL['Informativo'];
                                return (
                                    <tr key={m.id}>
                                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem', color: '#475569' }}>{m.descritor || '—'}</td>
                                        <td style={{ fontSize: '0.82rem' }}>{m.tema || '—'}</td>
                                        <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{m.requisito}</td>
                                        <td style={{ maxWidth: 380, fontSize: '0.84rem' }}>{(m.sumario || '').substring(0, 180)}{(m.sumario || '').length > 180 ? '…' : ''}</td>
                                        <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{m.estado || '—'}</td>
                                        <td><span data-liquid-tone={liquidTone(cor.bg)} data-liquid-badge="true" style={{ ...badge, background: cor.bg, color: cor.fg }}>{m.aplicavel || '—'}</span></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {editar && <ObrigacaoModal obrigacao={editar} onClose={() => setEditar(null)} onSaved={carregar} />}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };

export default RequisitosLegais;
